import { useMemo } from 'react';
import { DataTable } from '@/components/data-table/data-table';
import { StatsCard } from '@/components/ui/stats-card';
import { fmtDate, money, parseDay } from './flow-helpers';
import type { AssetFlow, FlowTx, MetricKey } from './flow-types';
import { AssetFlowTrendChart } from './flows-chart';
import { METRIC_COLORS, SHOW_TODAY_STATS } from './flows-constants';
import { FLOW_COLUMNS } from './flows-table-columns';
export interface FlowBlockProps {
  flow: AssetFlow;
  narrow: boolean;
}

export function AssetFlowTab({ flow }: FlowBlockProps) {
  const { unit } = flow;
  const data = useMemo<FlowTx[]>(() => flow.days.map((d) => ({ date: parseDay(d.date), incoming: d.incoming, outgoing: d.outgoing, net: d.incoming - d.outgoing })), [flow]);
  if (flow.error) {
    return <div>{flow.error}</div>;
  }
  const last = data[data.length - 1];
  const prev = data[data.length - 2];
  const maxAbs = Math.max(...data.map((d) => Math.abs(d.net))) || 1;
  const kdefs: [string, MetricKey][] = [
    ['Incoming', 'incoming'],
    ['Outgoing', 'outgoing'],
    ['Net flow', 'net'],
  ];

  return (
    <div>
      <section className="flex flex-col gap-3">
        <header className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Pending</h2>
        </header>
        <div className="flex flex-col gap-6 mb-4">
          <div className="grid gap-4 lg:grid-cols-3">
            <StatsCard borderLeftColor={METRIC_COLORS.incoming} label="Incoming" value={money(flow.pending.incoming, unit)} />
            <StatsCard borderLeftColor={METRIC_COLORS.outgoing} label="Outgoing" value={money(flow.pending.outgoing, unit)} />
            <StatsCard borderLeftColor={METRIC_COLORS.net} label="Net" value={money(flow.pending.incoming - flow.pending.outgoing, unit)} />
          </div>
        </div>
      </section>

      {SHOW_TODAY_STATS && (
        <section className="flex flex-col gap-3">
          <header className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">Today, {fmtDate(last.date)}</h2>
          </header>
          <div className="flex flex-col gap-6 mb-4">
            <div className="grid gap-4 lg:grid-cols-3">
              {kdefs.map(([title, k]) => {
                const diff = last[k] - prev[k];
                const good = k === 'outgoing' ? diff <= 0 : diff >= 0;
                return (
                  <StatsCard
                    borderLeftColor={METRIC_COLORS[k]}
                    key={title}
                    label={title}
                    subtitle={`${diff >= 0 ? '▲' : '▼'} ${money(Math.abs(diff), unit)} vs ${fmtDate(prev.date)}`}
                    negative={!good}
                    value={money(last[k], unit)}
                  />
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="flex flex-col gap-3">
        <header className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Trend</h2>
        </header>
        <AssetFlowTrendChart data={data} unit={unit} label={flow.label} />
      </section>

      <section className="flex flex-col gap-3 mb-3">
        <header className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Daily timeline</h2>
        </header>
        <DataTable columns={FLOW_COLUMNS(maxAbs, unit)} data={[...data].reverse()} emptyMessage="No data" />
      </section>
    </div>
  );
}
